import React, { useState } from 'react';
import { Mail, Users, CheckCircle, User, Briefcase } from 'lucide-react';

const VerticalContact = ({
  waitlistType,
  heading = 'Request a Demo',
  body = 'Tell us about your practice and we will show you Sortio working with a sample of your files.',
  orgLabel = 'Organization Name',
  orgPlaceholder = 'Acme LLC',
  sizeLabel = 'Team Size',
  sizeOptions = [
    { value: 'solo', label: 'Solo' },
    { value: '2-10', label: '2-10' },
    { value: '11-50', label: '11-50' },
    { value: '50+', label: '50+' },
  ],
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [orgName, setOrgName] = useState('');
  const [orgSize, setOrgSize] = useState(sizeOptions[0].value);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const endpoint = 'https://app.getsortio.com/api/sortio-waitlist/';

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          waitlist_type: waitlistType,
          name: orgName ? `${name} (${orgName})` : name,
          email,
          company_size: orgSize,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setSubmitted(true);
        setName('');
        setEmail('');
        setOrgName('');
        setOrgSize(sizeOptions[0].value);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    }

    setIsSubmitting(false);
  };

  return (
    <section id="contact" className="my-20">
      <div className="text-center mb-12">
        <h2 className="text-3xl sm:text-4xl font-semibold mb-4 tracking-tight text-apple-50">
          {heading.includes('Demo') ? (
            <>Request a <span className="apple-gradient-text">Demo</span></>
          ) : heading}
        </h2>
        <p className="text-apple-300 text-lg font-light max-w-2xl mx-auto">{body}</p>
      </div>

      <div className="bg-apple-700/30 backdrop-blur-md p-8 sm:p-10 rounded-3xl border border-apple-600/30 relative overflow-hidden max-w-2xl mx-auto">
        <div className="absolute -left-40 -top-40 w-80 h-80 rounded-full bg-sortio-primary/10 opacity-30 blur-[100px]" />
        <div className="absolute -right-40 -bottom-40 w-80 h-80 rounded-full bg-sortio-secondary/10 opacity-30 blur-[100px]" />

        {!submitted ? (
          <form onSubmit={handleSubmit} className="relative z-10">
            <div className="mb-5">
              <label htmlFor="vc-name" className="block text-sm font-medium mb-2 text-apple-100">Full Name</label>
              <div className="relative">
                <User className="absolute left-4 top-3.5 text-apple-400" size={16} />
                <input type="text" id="vc-name" className="w-full px-4 py-3 pl-10 rounded-xl bg-apple-800/70 border border-apple-600/60 text-apple-50 focus:outline-none focus:ring-2 focus:ring-sortio-primary/50 focus:border-sortio-primary/50 transition-all" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
            </div>

            <div className="mb-5">
              <label htmlFor="vc-email" className="block text-sm font-medium mb-2 text-apple-100">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-3.5 text-apple-400" size={16} />
                <input type="email" id="vc-email" className="w-full px-4 py-3 pl-10 rounded-xl bg-apple-800/70 border border-apple-600/60 text-apple-50 focus:outline-none focus:ring-2 focus:ring-sortio-primary/50 focus:border-sortio-primary/50 transition-all" placeholder="you@work.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
            </div>

            <div className="mb-5">
              <label htmlFor="vc-org" className="block text-sm font-medium mb-2 text-apple-100">{orgLabel} <span className="text-apple-400 text-xs">(Optional)</span></label>
              <div className="relative">
                <Briefcase className="absolute left-4 top-3.5 text-apple-400" size={16} />
                <input type="text" id="vc-org" className="w-full px-4 py-3 pl-10 rounded-xl bg-apple-800/70 border border-apple-600/60 text-apple-50 focus:outline-none focus:ring-2 focus:ring-sortio-primary/50 focus:border-sortio-primary/50 transition-all" placeholder={orgPlaceholder} value={orgName} onChange={(e) => setOrgName(e.target.value)} />
              </div>
            </div>

            <div className="mb-7">
              <label htmlFor="vc-size" className="block text-sm font-medium mb-2 text-apple-100">{sizeLabel}</label>
              <div className="relative">
                <Users className="absolute left-4 top-3.5 text-apple-400" size={16} />
                <select id="vc-size" className="w-full px-4 py-3 pl-10 rounded-xl bg-apple-800/70 border border-apple-600/60 text-apple-50 focus:outline-none focus:ring-2 focus:ring-sortio-primary/50 focus:border-sortio-primary/50 transition-all appearance-none" value={orgSize} onChange={(e) => setOrgSize(e.target.value)} required>
                  {sizeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <button type="submit" className="w-full bg-gradient-to-r from-sortio-primary to-sortio-primary/80 text-white font-medium px-8 py-4 text-base rounded-xl shadow-lg shadow-sortio-primary/20 hover:shadow-sortio-primary/30 hover:brightness-110 transition-all duration-300 flex items-center justify-center group" disabled={isSubmitting}>
              {isSubmitting ? 'Processing...' : 'Request a Demo'}
            </button>

            {submitStatus === 'error' && (
              <div className="mt-4 p-3 bg-gradient-to-r from-red-500/80 to-red-600/60 text-white rounded-lg flex items-center text-sm">
                <span>Something went wrong. Please try again or email marcus@getsortio.com.</span>
              </div>
            )}

            <p className="text-apple-400 text-xs text-center mt-4">
              By submitting, you agree to be contacted about Sortio. We respect your privacy.
            </p>
          </form>
        ) : (
          <div className="text-center py-8 relative z-10">
            <div className="w-20 h-20 rounded-full bg-sortio-secondary/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-sortio-secondary" />
            </div>
            <h3 className="text-2xl font-medium mb-3 text-apple-50">Thank you</h3>
            <p className="text-apple-300">
              We received your request. A member of our team will reach out shortly to schedule a
              demo.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default VerticalContact;
